import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SidePanel } from "@dashboard/shared/components/SidePanel/SidePanel";
import { getStatusIndicatorData } from "@dashboard/shared/subscription/subscriptionStatus";
import { Box, TableRow, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import TimeService from "@services/timeService/timeService";
import UserSettingsService from "@services/userSettingsService";
import { SecondaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader, TruncatedText } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { Tooltip } from "@shared/Tooltip";
import { ColumnDef, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { useCreateClientMutation } from "../api";
import { useCurrentWorkspaceClients } from "../api/helpers";
import { CreateClientPayload } from "../api/rtkClient";
import { ResellerClient } from "../types";

import AddClientDialog from "./components/AddClientDialog";
import TableItem from "./components/TableItem";

const useStyles = makeStyles<{ statusColor?: string }>()((muiTheme: Theme, { statusColor }) => ({
    licenseSummaryIndicator: {
        backgroundColor: statusColor,
        width: muiTheme.spacing(1),
        borderRadius: "24px",
        height: muiTheme.spacing(8),
    },
    statusCell: {
        display: "flex",
        alignItems: "center",
        padding: muiTheme.spacing(1, 2),
        width: "100%",
        minHeight: muiTheme.spacing(6),
    },
    buttonContainer: {
        marginBottom: muiTheme.spacing(2),
        display: "flex",
        justifyContent: "flex-end",
    },
}));

const getTableColumns = (theme: Theme): ColumnDef<ResellerClient, unknown>[] => [
    {
        header: "",
        accessorKey: "statusIndicator",
        accessorFn: (row) => {
            return getStatusIndicatorData(row.subscription.status, theme);
        },
        cell: (info) => {
            const StatusCell = () => {
                const statusData = info.getValue<{ tooltip: string; color: string }>();
                const { classes } = useStyles({ statusColor: statusData.color });

                return (
                    <div className={classes.statusCell}>
                        <Tooltip
                            placement="right"
                            title={statusData.tooltip}
                            childrenBoxStyles={{ display: "block", width: "100%", height: "100%" }}
                        >
                            <div className={classes.licenseSummaryIndicator} />
                        </Tooltip>
                    </div>
                );
            };
            return <StatusCell />;
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            return rowA.original.subscription.status.localeCompare(rowB.original.subscription.status);
        },
    },
    {
        header: i18next.t("pages:clientsOverviewPage.table.header.organization"),
        accessorKey: "organizationName",
        cell: (info) => {
            const organizationName = info.getValue<string>();
            return <TruncatedText title={organizationName} displayValue={organizationName || "-"} maxWidth="32ch" />;
        },
        enableSorting: true,
    },
    {
        header: i18next.t("pages:clientsOverviewPage.table.header.status"),
        accessorKey: "status",
        accessorFn: (row) => row.subscription.status,
        cell: (info) => info.getValue<string>(),
        enableSorting: true,
    },
    {
        header: i18next.t("common:label.description"),
        accessorKey: "resellerDescription",
        cell: (info) => {
            const description = info.getValue<string | null>();
            return <TruncatedText title={description ?? ""} displayValue={description || "-"} />;
        },
        enableSorting: false,
        meta: { fluid: true, fluidMinWidth: "24ch" },
    },
    {
        header: i18next.t("pages:clientsOverviewPage.table.header.creation"),
        accessorKey: "created",
        accessorFn: (row) => row.subscription.creationDate,
        cell: (info) => {
            const creationDate = info.getValue<string>();
            return creationDate ? (
                <Tooltip title={TimeService.getLocalDate(creationDate, "ISO", "dateTime")}>
                    <div>{TimeService.getLocalDate(creationDate, "ISO", "date")}</div>
                </Tooltip>
            ) : (
                "-"
            );
        },
        enableSorting: true,
    },
    {
        header: i18next.t("pages:clientsOverviewPage.table.header.expiration"),
        accessorKey: "expired",
        accessorFn: (row) => {
            return row.subscription.expirationDate;
        },
        cell: (info) =>
            info.getValue() ? (
                <Tooltip title={TimeService.getLocalDate(info.getValue<string>(), "ISO", "dateTime")}>
                    <div>{TimeService.getLocalDate(info.getValue<string>(), "ISO", "date")}</div>
                </Tooltip>
            ) : (
                "-"
            ),
        enableSorting: true,
    },
    {
        header: i18next.t("pages:clientsOverviewPage.table.header.cancellation"),
        accessorKey: "cancelled",
        accessorFn: (row) => {
            return row.subscription.cancellationDate;
        },
        cell: (info) =>
            info.getValue() ? (
                <Tooltip title={TimeService.getLocalDate(info.getValue<string>(), "ISO", "dateTime")}>
                    <div>{TimeService.getLocalDate(info.getValue<string>(), "ISO", "date")}</div>
                </Tooltip>
            ) : (
                "-"
            ),
        enableSorting: true,
    },
];

const ClientsOverviewPage = () => {
    const { t } = useTranslation(["pages"]);
    const { classes, theme } = useStyles({});
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: UserSettingsService.getClientsPageRowsPerPage(),
    });
    const [sorting, setSorting] = useState([{ id: "status", desc: false }]);

    const sortParams = useMemo(() => {
        if (sorting.length === 0) {
            return {};
        }

        const primarySort = sorting[0];

        return {
            sortCriteria: primarySort.id,
            sortDirection: primarySort.desc ? "descending" : "ascending",
        };
    }, [sorting]);

    const queryParams = useMemo(
        () => ({ pageIndex: pagination.pageIndex, pageSize: pagination.pageSize, ...sortParams }),
        [pagination.pageIndex, pagination.pageSize, sortParams],
    );

    const { data: clientsData, isFetching, isLoading, isError, refetch } = useCurrentWorkspaceClients(queryParams);

    const tableLoading = useTableFetchLoading(isFetching, isLoading, queryParams);

    const [createClient] = useCreateClientMutation();

    const handleClientCreated = (payload: CreateClientPayload) => createClient(payload).unwrap();

    const tableData = clientsData?.list || [];

    const table = useReactTable<ResellerClient>({
        data: tableData,
        columns: getTableColumns(theme),
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        pageCount: clientsData ? Math.ceil(clientsData.count / pagination.pageSize) : 0,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, tableData.length]);

    return (
        <PageContainer fitViewport>
            <ExpiredWrapper>
                <Box className={layoutClasses.tableLayout}>
                    <Box className={classes.buttonContainer}>
                        <SecondaryButton
                            startIcon={<i className="fa-solid fa-plus" />}
                            size="small"
                            onClick={() => setIsAddClientDialogOpen(true)}
                        >
                            {t("pages:clientsOverviewPage.button.addClient")}
                        </SecondaryButton>
                    </Box>

                    <Box className={layoutClasses.tableWrapper}>
                        <MinimalTableContainer
                            ref={tableContainerRef}
                            className={cx(
                                layoutClasses.tableContainer,
                                !contentOverflows && layoutClasses.tableContainerNoScroll,
                            )}
                        >
                            <MinimalTable>
                                <MinimalTableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <Header key={header.id} {...header} />
                                            ))}
                                        </TableRow>
                                    ))}
                                </MinimalTableHeader>
                                <TableBody
                                    table={table}
                                    loading={tableLoading}
                                    noDataMessage={t("pages:clientsOverviewPage.table.noData")}
                                    error={{ isError, onError: refetch }}
                                >
                                    {table.getRowModel().rows.map((row, index) => (
                                        <TableItem key={row.id} row={row} index={index} />
                                    ))}
                                </TableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                    </Box>
                    {!tableLoading && !isError && (
                        <CustomTablePagination
                            component="div"
                            className={layoutClasses.pagination}
                            rowsPerPageOptions={[12, 24, 48]}
                            count={clientsData?.count || 0}
                            rowsPerPage={pageSize}
                            page={pageIndex}
                            onPageChange={(_event, page) => table.setPageIndex(page)}
                            onRowsPerPageChange={(e) => {
                                const size = e.target.value ? Number(e.target.value) : 12;
                                table.setPageSize(size);
                                UserSettingsService.setWorkspaceValue("clientsPage.rowsPerPage", size);
                            }}
                        />
                    )}
                </Box>
            </ExpiredWrapper>
            <AddClientDialog
                open={isAddClientDialogOpen}
                onClose={() => setIsAddClientDialogOpen(false)}
                onClientCreated={handleClientCreated}
            />
            <SidePanel />
        </PageContainer>
    );
};

export default withMetaDescription(ClientsOverviewPage, {
    title: i18next.t("pages:clientsOverviewPage.metaTitle"),
    description: i18next.t("pages:clientsOverviewPage.metaDescription"),
});
